import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Calendar, Plus, MessageCircle, ListChecks, ArrowRight, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

export default function OneOnOnes() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null); // 1:1 detail dialog
  const [openSchedule, setOpenSchedule] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/one-on-ones", { params: { upcoming: tab === "upcoming" } });
      setItems(r.data.items || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab]);

  if (!user?.employee_id) {
    return <div className="axia-card p-8 text-sm text-slate-500">Your account isn't linked to an employee record yet.</div>;
  }

  return (
    <div className="max-w-5xl space-y-6" data-testid="one-on-ones-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Continuous performance</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">1:1s</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">Lightweight scheduled check-ins with shared agenda and action items. The single highest-leverage manager habit.</p>
        </div>
        <Dialog open={openSchedule} onOpenChange={setOpenSchedule}>
          <DialogTrigger asChild>
            <Button data-testid="schedule-1on1-btn"><Plus className="w-4 h-4 mr-2" /> Schedule 1:1</Button>
          </DialogTrigger>
          <ScheduleDialog onClose={() => { setOpenSchedule(false); reload(); }} />
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="pt-4">
          {loading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="axia-card p-12 text-center text-slate-500">
              <Calendar className="w-6 h-6 mx-auto text-slate-300" />
              <div className="font-display text-lg font-semibold text-slate-900 mt-3">No 1:1s {tab === "upcoming" ? "scheduled" : ""}</div>
              <p className="text-sm mt-1">Schedule a recurring 1:1 with your manager or a direct report.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((o) => {
                const otherName = o.employee_id === user.employee_id ? o.manager_name : o.employee_name;
                const role = o.employee_id === user.employee_id ? "with manager" : "with report";
                return (
                  <button
                    key={o.id}
                    onClick={() => setOpen(o)}
                    className="w-full text-left axia-card p-5 hover:border-slate-400 transition flex items-center gap-4"
                    data-testid={`oneonone-row-${o.id}`}
                  >
                    <div className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                      {(otherName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-slate-900">{otherName}</div>
                      <div className="text-xs text-slate-500">{role} · {o.duration_min}m</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono text-sm text-slate-700">{o.scheduled_for}</div>
                      <div className="flex items-center gap-2 mt-1 justify-end text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3" /> {o.agenda?.length || 0}</span>
                        <span className="inline-flex items-center gap-0.5"><ListChecks className="w-3 h-3" /> {o.action_items?.length || 0}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          {open && <OneOnOneDetail oneOnOne={open} onChanged={async () => { await reload(); const r = await api.get(`/one-on-ones`); const fresh = r.data.items?.find?.((x) => x.id === open.id); if (fresh) setOpen(fresh); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ScheduleDialog({ onClose }) {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [duration, setDuration] = useState(30);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/employees", { params: { limit: 200 } }).then((r) => setEmployees(r.data.items || []));
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/one-on-ones", {
        employee_id: employeeId || null,
        manager_id: managerId || null,
        scheduled_for: date,
        duration_min: Number(duration),
      });
      toast.success("1:1 scheduled");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent data-testid="schedule-dialog">
      <DialogHeader>
        <DialogTitle>Schedule 1:1</DialogTitle>
        <DialogDescription>Default schedules between you and your reporting manager. Override if needed.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label>Date</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="schedule-date" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Duration (min)</Label>
            <Input type="number" min={15} step={15} value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="schedule-duration" />
          </div>
        </div>
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-600">Override participants (optional)</summary>
          <div className="mt-2 grid grid-cols-1 gap-2">
            <div>
              <Label>Employee</Label>
              <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)} className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm">
                <option value="">Me</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Manager / Other party</Label>
              <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm">
                <option value="">Auto (from reporting line)</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                ))}
              </select>
            </div>
          </div>
        </details>
      </div>
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="schedule-submit">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Schedule
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function OneOnOneDetail({ oneOnOne, onChanged }) {
  const { user } = useAuth();
  const [agendaInput, setAgendaInput] = useState("");
  const [actionInput, setActionInput] = useState("");
  const [notes, setNotes] = useState(oneOnOne.notes || "");
  const [saving, setSaving] = useState(false);

  const addAgenda = async () => {
    if (!agendaInput.trim()) return;
    await api.post(`/one-on-ones/${oneOnOne.id}/items`, { type: "agenda", body: agendaInput });
    setAgendaInput("");
    onChanged?.();
  };
  const addAction = async () => {
    if (!actionInput.trim()) return;
    await api.post(`/one-on-ones/${oneOnOne.id}/items`, { type: "action", body: actionInput });
    setActionInput("");
    onChanged?.();
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`/one-on-ones/${oneOnOne.id}`, { notes });
      toast.success("Notes saved");
      onChanged?.();
    } finally {
      setSaving(false);
    }
  };

  const markComplete = async () => {
    await api.patch(`/one-on-ones/${oneOnOne.id}`, { status: "completed" });
    toast.success("Marked complete");
    onChanged?.();
  };

  const otherName = oneOnOne.employee_id === user.employee_id ? oneOnOne.manager_name : oneOnOne.employee_name;

  return (
    <>
      <DialogHeader>
        <DialogTitle>1:1 with {otherName}</DialogTitle>
        <DialogDescription>{oneOnOne.scheduled_for} · {oneOnOne.duration_min}m</DialogDescription>
      </DialogHeader>
      <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
        <section>
          <div className="axia-overline mb-2">Shared agenda</div>
          <ul className="space-y-1.5">
            {(oneOnOne.agenda || []).map((a) => (
              <li key={a.id} className="text-sm border border-slate-200 rounded-md px-3 py-2 bg-white" data-testid={`agenda-${a.id}`}>
                <span className="text-slate-700">{a.body}</span>
              </li>
            ))}
            {(oneOnOne.agenda || []).length === 0 && <li className="text-sm text-slate-400">No agenda items yet.</li>}
          </ul>
          <div className="mt-2 flex gap-2">
            <Input value={agendaInput} onChange={(e) => setAgendaInput(e.target.value)} placeholder="Add a topic to discuss…" data-testid="agenda-input" onKeyDown={(e) => e.key === "Enter" && addAgenda()} />
            <Button onClick={addAgenda} variant="outline" size="sm" data-testid="add-agenda-btn">Add</Button>
          </div>
        </section>

        <section>
          <div className="axia-overline mb-2">Action items</div>
          <ul className="space-y-1.5">
            {(oneOnOne.action_items || []).map((a) => (
              <li key={a.id} className="text-sm border border-slate-200 rounded-md px-3 py-2 bg-white flex items-center gap-2" data-testid={`action-${a.id}`}>
                <span className={`axia-pill ${a.status === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{a.status}</span>
                <span className="text-slate-700">{a.body}</span>
                {a.due_date && <span className="ml-auto font-mono text-xs text-slate-500">due {a.due_date}</span>}
              </li>
            ))}
            {(oneOnOne.action_items || []).length === 0 && <li className="text-sm text-slate-400">No action items yet.</li>}
          </ul>
          <div className="mt-2 flex gap-2">
            <Input value={actionInput} onChange={(e) => setActionInput(e.target.value)} placeholder="New action item…" data-testid="action-input" onKeyDown={(e) => e.key === "Enter" && addAction()} />
            <Button onClick={addAction} variant="outline" size="sm" data-testid="add-action-btn">Add</Button>
          </div>
        </section>

        <section>
          <div className="axia-overline mb-2">Notes</div>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Capture key points from the conversation." data-testid="notes-textarea" />
        </section>
      </div>
      <DialogFooter className="gap-2">
        {oneOnOne.status === "scheduled" && (
          <Button variant="outline" onClick={markComplete} data-testid="mark-complete-btn">
            <Check className="w-3.5 h-3.5 mr-1.5" /> Mark complete
          </Button>
        )}
        <Button onClick={saveNotes} disabled={saving} data-testid="save-notes-btn">
          {saving && <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />} Save notes
        </Button>
      </DialogFooter>
    </>
  );
}
